import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ClassItem } from '../../data/classes';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { Button } from '@/components/shared/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useClassDetail } from '@/data/queries/useClasses';
import { ROUTES } from '@/lib/constants/routes';
import { classDetailDtoToClassItem } from '@/utils/classUtils';
import { handleApiError } from '@/utils/errorHandlers';
import { ApiError } from '@/services/apiClient';

export default function ClassCurriculum(): React.ReactElement {
    const { t } = useTranslation('class');
    const { classId: classIdParam } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const { getCurrentRole } = useAuth();

    const role = getCurrentRole();
    const isManagerPath = role === 'master' || role === 'admin';
    const basePath = role === 'master' ? '/master' : role === 'admin' ? '/admin' : '/student';

    const classId = parseInt((classIdParam ?? '').trim(), 10);

    const {
        data: classDetailDto,
        isError,
        error: queryError,
        refetch,
    } = useClassDetail(classId, {
        enabled: classId > 0,
    });

    const classItem: ClassItem | null = useMemo(() => {
        if (classDetailDto) {
            try {
                return classDetailDtoToClassItem(classDetailDto);
            } catch {
                return null;
            }
        }
        return null;
    }, [classDetailDto]);

    const backToClassDetailPath = (): string => {
        if (isManagerPath) {
            return `${basePath}/class-management/${classId}`;
        }
        if (role === 'master') {
            return ROUTES.MASTER.CLASS_DETAIL(String(classId));
        }
        if (role === 'admin') {
            return ROUTES.ADMIN.CLASS_DETAIL(String(classId));
        }
        return `${ROUTES.STUDENT.BASE}/my-classes/${classId}`;
    };

    useEffect(() => {
        if (!classItem?.curriculum?.length) return;
        const firstSession = classItem.curriculum[0];
        const sessionPath = isManagerPath
            ? `${basePath}/class-management/${classId}/curriculum/${firstSession.id}`
            : `${basePath}/my-classes/${classId}/curriculum/${firstSession.id}`;
        navigate(sessionPath, { replace: true });
    }, [classId, classItem, isManagerPath, basePath, navigate]);

    /** API 경로: 응답 전까지는 로딩(이전 useMemo가 pending 구간에 null을 내 던 문제 방지) */
    if (!classDetailDto && !isError) {
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader title="…" onBack={() => navigate(backToClassDetailPath())} />
                }
            >
                <div className="flex items-center justify-center h-full text-gray-500">{t('myClass.loading')}</div>
            </FullScreenLayout>
        );
    }

    if (isError) {
        const fallback = t('detail.classNotFound');
        let message = fallback;
        if (queryError instanceof ApiError) {
            if (queryError.status === 401) {
                message = t('detail.classLoadAuthRequiredPrivate');
            } else if (queryError.status === 403) {
                message = t('detail.classLoadForbiddenPrivate');
            } else {
                message = handleApiError(queryError, fallback).message;
            }
        } else if (queryError instanceof Error) {
            message = handleApiError(queryError, fallback).message;
        }
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader title={t('shared.curriculum')} onBack={() => navigate(backToClassDetailPath())} />
                }
            >
                <div className="flex flex-col items-center justify-center gap-4 px-6 py-12 text-center text-gray-700">
                    <p>{message}</p>
                    <Button type="button" variant="outline" onClick={() => refetch()}>
                        {t('myClass.retry')}
                    </Button>
                </div>
            </FullScreenLayout>
        );
    }

    if (!classItem) {
        return (
            <div className="p-8">
                {t('detail.classNotFound')}
            </div>
        );
    }

    const showApiNotConnectedBadge = !classDetailDto;

    if (!classItem.curriculum || classItem.curriculum.length === 0) {
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader
                        title={classItem.title}
                        onBack={() => navigate(backToClassDetailPath())}
                        rightContent={
                            showApiNotConnectedBadge && (
                                <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-semibold border border-red-300">
                                    {t('session.apiNotConnectedBadge')}
                                </span>
                            )
                        }
                    />
                }
            >
                <div className="flex items-center justify-center h-full text-gray-400">{t('detail.noSessionsAlert')}</div>
            </FullScreenLayout>
        );
    }

    return (
        <FullScreenLayout
            header={
                <FullScreenHeader
                    title={classItem.title}
                    onBack={() => navigate(backToClassDetailPath())}
                    rightContent={
                        showApiNotConnectedBadge && (
                            <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-semibold border border-red-300">
                                {t('session.apiNotConnectedBadge')}
                            </span>
                        )
                    }
                />
            }
        >
            <div className="flex items-center justify-center h-full text-gray-400">{t('session.redirectingToSession')}</div>
        </FullScreenLayout>
    );
}
